import { c as _c } from "react/compiler-runtime";
import { IcoChevronDown16, IcoChevronLeft16, IcoChevronRight16, IcoClose16 } from '@onefootprint/icons';
import type { ChatSessionResponse } from '@onefootprint/request-types/dashboard';
import { cx } from 'class-variance-authority';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useDocumentsPanelOpen from '../../../hooks/use-documents-panel-open';
import { isErrorTaskStatus } from '../../../utils/journey-attribution';
import ChatPanel from '../../chat-panel';
import { type BackgroundAgent, useBackgroundAgents } from '../background-agents-context';

// A read-only ChatPanel needs a session object but never uses its fields; empty is fine.
const PASSIVE_SESSION: ChatSessionResponse = {
  agentSandboxLogsUrl: '',
  workspaceSandboxLogsUrl: '',
  createdAt: '',
  modalBaseUrl: '',
  sessionId: '',
  sessionToken: '',
  wsUrl: ''
};
type BackgroundAgentsPanelProps = {
  /** Override the store — for stories/tests. */
  agents?: BackgroundAgent[];
  onClose?: () => void;
  /** Open straight into a sub-agent's transcript — for stories/snapshots. */
  defaultSelectedId?: string;
};

// Phone sizes match the widget header this panel slides over, so the two read as one bar.
const iconButtonClass = 'flex items-center justify-center size-11 md:size-7 rounded-(--icon-button-border-radius) hover:bg-hover-control cursor-pointer focus-visible:outline focus-visible:outline-offset-2 focus-visible:outline-primary';
const formatDuration = (ms: number): string => {
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds % 60}s`;
};
const AgentCard = t0 => {
  const $ = _c(35);
  const {
    agent,
    onViewTranscript
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "chat.backgroundAgents"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("copilot", t1);
  let t2;
  if ($[1] !== agent.status) {
    t2 = isErrorTaskStatus(agent.status);
    $[1] = agent.status;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const error = t2;
  let t3;
  if ($[3] !== agent.running || $[4] !== error || $[5] !== t) {
    t3 = agent.running ? t(_temp) : error ? t(_temp2) : t(_temp3);
    $[3] = agent.running;
    $[4] = error;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const statusLabel = t3;
  let t4;
  if ($[7] !== agent.label) {
    t4 = <p className="text-label-3 text-primary truncate">{agent.label}</p>;
    $[7] = agent.label;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = error ? "text-error" : "text-tertiary";
  const t6 = agent.running && "text-shimmer";
  let t7;
  if ($[9] !== t5 || $[10] !== t6) {
    t7 = cx(t5, t6);
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== statusLabel || $[13] !== t7) {
    t8 = <span className={t7}>{statusLabel}</span>;
    $[12] = statusLabel;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== agent.durationMs) {
    t9 = agent.durationMs > 0 && <span className="text-quaternary">{formatDuration(agent.durationMs)}</span>;
    $[15] = agent.durationMs;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <div className="flex items-center gap-1 text-caption-1">{t8}{t9}</div>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== agent.toolUses || $[21] !== t) {
    t11 = agent.toolUses > 0 && <span className="text-quaternary">{t(_temp4, {
        count: agent.toolUses
      })}</span>;
    $[20] = agent.toolUses;
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t) {
    t12 = t(_temp5);
    $[23] = t;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== onViewTranscript || $[26] !== t12) {
    t13 = <button type="button" onClick={onViewTranscript} className="ml-auto text-accent hover:underline cursor-pointer">{t12}</button>;
    $[25] = onViewTranscript;
    $[26] = t12;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== t11 || $[29] !== t13) {
    t14 = <div className="flex items-center gap-2 text-caption-1">{t11}{t13}</div>;
    $[28] = t11;
    $[29] = t13;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t10 || $[32] !== t14 || $[33] !== t4) {
    t15 = <div className="flex flex-col gap-1 rounded-default border border-solid border-tertiary bg-primary p-3">{t4}{t10}{t14}</div>;
    $[31] = t10;
    $[32] = t14;
    $[33] = t4;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  return t15;
};
const Section = t0 => {
  const $ = _c(19);
  const {
    title,
    agents,
    onViewTranscript
  } = t0;
  const [open, setOpen] = useState(true);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(_temp6);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== title) {
    t2 = <span>{title}</span>;
    $[1] = title;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== agents.length) {
    t3 = <span className="text-quaternary tabular-nums">{agents.length}</span>;
    $[3] = agents.length;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== open) {
    t4 = <span className="shrink-0 text-quaternary group-hover:text-tertiary">{open ? <IcoChevronDown16 /> : <IcoChevronRight16 />}</span>;
    $[5] = open;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== open || $[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <button type="button" onClick={t1} aria-expanded={open} className="group flex items-center gap-1 w-full text-label-3 text-tertiary hover:text-secondary cursor-pointer">{t2}{t3}{t4}</button>;
    $[7] = open;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== agents || $[13] !== onViewTranscript || $[14] !== open) {
    t6 = open && agents.map(agent => <AgentCard key={agent.id} agent={agent} onViewTranscript={() => onViewTranscript(agent.id)} />);
    $[12] = agents;
    $[13] = onViewTranscript;
    $[14] = open;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <div className="flex flex-col gap-2">{t5}{t6}</div>;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
};

// The frames are attributed to the spawn but the spawn isn't among them, so buildSegments renders
// them inline (not re-nested in a journey card).
const AgentTranscript = t0 => {
  const $ = _c(10);
  const {
    agent,
    onOpenAgent
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "chat.backgroundAgents"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("copilot", t1);
  let t2;
  if ($[1] !== agent.prompt) {
    t2 = agent.prompt && <div className="mx-4 md:mx-8 mt-8 rounded-default bg-secondary p-3 text-body-3 text-secondary whitespace-pre-wrap wrap-anywhere">{agent.prompt}</div>;
    $[1] = agent.prompt;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== agent.frames || $[4] !== onOpenAgent || $[5] !== t) {
    t3 = agent.frames.length === 0 ? <p className="p-3 text-body-3 text-tertiary">{t(_temp7)}</p> : <ChatPanel readOnly={true} messages={agent.frames} session={PASSIVE_SESSION} isConnected={false} isThinking={false} onSendMessage={_temp8} onInterrupt={_temp9} onOpenAgent={onOpenAgent} />;
    $[3] = agent.frames;
    $[4] = onOpenAgent;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">{t2}{t3}</div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
};

// Agent-centric mode of the shared sidebar: sub-agents as status cards, each drilling into its
// transcript. Reads the module store unless `agents` overrides (stories).
const BackgroundAgentsPanel = t0 => {
  const $ = _c(54);
  const {
    agents,
    onClose,
    defaultSelectedId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "chat.backgroundAgents"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("copilot", t1);
  const stored = useBackgroundAgents();
  const items = agents ?? stored.items;
  const {
    agentTarget,
    clearAgentTarget
  } = useDocumentsPanelOpen();
  const [selectedId, setSelectedId] = useState(defaultSelectedId ?? null);
  let t2;
  let t3;
  if ($[1] !== agentTarget || $[2] !== clearAgentTarget) {
    t2 = () => {
      if (agentTarget) {
        setSelectedId(agentTarget);
        clearAgentTarget();
      }
    };
    t3 = [agentTarget, clearAgentTarget];
    $[1] = agentTarget;
    $[2] = clearAgentTarget;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== items || $[6] !== selectedId) {
    t4 = items.find(agent => agent.id === selectedId) ?? null;
    $[5] = items;
    $[6] = selectedId;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const selected = t4;
  if (selected) {
    const t5 = selected.label;
    let t6;
    if ($[8] !== t) {
      t6 = t(_temp10);
      $[8] = t;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    let t7;
    let t8;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = () => setSelectedId(null);
      t8 = <IcoChevronLeft16 />;
      $[10] = t7;
      $[11] = t8;
    } else {
      t7 = $[10];
      t8 = $[11];
    }
    let t9;
    if ($[12] !== t6) {
      t9 = <button type="button" aria-label={t6} className={iconButtonClass} onClick={t7}>{t8}</button>;
      $[12] = t6;
      $[13] = t9;
    } else {
      t9 = $[13];
    }
    let t10;
    if ($[14] !== selected.label) {
      t10 = <span className="flex-1 min-w-0 text-label-3 font-medium text-primary truncate">{selected.label}</span>;
      $[14] = selected.label;
      $[15] = t10;
    } else {
      t10 = $[15];
    }
    let t11;
    if ($[16] !== onClose || $[17] !== t) {
      t11 = onClose && <button type="button" aria-label={t(_temp11)} className={iconButtonClass} onClick={onClose}><IcoClose16 /></button>;
      $[16] = onClose;
      $[17] = t;
      $[18] = t11;
    } else {
      t11 = $[18];
    }
    let t12;
    if ($[19] !== t10 || $[20] !== t11 || $[21] !== t9) {
      t12 = <div className="flex items-center gap-1 border-b border-tertiary pl-1 pr-2 h-12 md:pl-2 md:pr-3 md:h-11 shrink-0">{t9}{t10}{t11}</div>;
      $[19] = t10;
      $[20] = t11;
      $[21] = t9;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    let t13;
    if ($[23] !== selected) {
      t13 = <AgentTranscript agent={selected} />;
      $[23] = selected;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    let t14;
    if ($[25] !== selected.label || $[26] !== t12 || $[27] !== t13) {
      t14 = <div role="complementary" aria-label={t5} className="flex flex-col h-full w-full bg-primary overflow-hidden">{t12}{t13}</div>;
      $[25] = selected.label;
      $[26] = t12;
      $[27] = t13;
      $[28] = t14;
    } else {
      t14 = $[28];
    }
    return t14;
  }
  let t5;
  if ($[29] !== items) {
    t5 = items.filter(_temp12);
    $[29] = items;
    $[30] = t5;
  } else {
    t5 = $[30];
  }
  const running = t5;
  let t6;
  if ($[31] !== items) {
    t6 = items.filter(_temp13);
    $[31] = items;
    $[32] = t6;
  } else {
    t6 = $[32];
  }
  const finished = t6;
  let t7;
  if ($[33] !== t) {
    t7 = t(_temp14);
    $[33] = t;
    $[34] = t7;
  } else {
    t7 = $[34];
  }
  let t8;
  if ($[35] !== t) {
    t8 = t(_temp15);
    $[35] = t;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== t8) {
    t9 = <span className="flex-1 min-w-0 text-label-3 font-medium text-primary truncate ml-1">{t8}</span>;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== onClose || $[40] !== t) {
    t10 = onClose && <button type="button" aria-label={t(_temp16)} className={iconButtonClass} onClick={onClose}><IcoClose16 /></button>;
    $[39] = onClose;
    $[40] = t;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== t10 || $[43] !== t9) {
    t11 = <div className="flex items-center gap-1 border-b border-tertiary px-2 md:px-3 h-12 md:h-11 shrink-0">{t9}{t10}</div>;
    $[42] = t10;
    $[43] = t9;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== finished || $[46] !== items.length || $[47] !== running || $[48] !== t) {
    t12 = items.length === 0 ? <div className="flex flex-1 items-center justify-center p-6 text-center"><p className="text-body-3 text-tertiary">{t(_temp17)}</p></div> : <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto [scrollbar-gutter:stable] p-3">{running.length > 0 && <Section title={t(_temp18)} agents={running} onViewTranscript={setSelectedId} />}{finished.length > 0 && <Section title={t(_temp19)} agents={finished} onViewTranscript={setSelectedId} />}</div>;
    $[45] = finished;
    $[46] = items.length;
    $[47] = running;
    $[48] = t;
    $[49] = t12;
  } else {
    t12 = $[49];
  }
  let t13;
  if ($[50] !== t11 || $[51] !== t12 || $[52] !== t7) {
    t13 = <div role="complementary" aria-label={t7} className="flex flex-col h-full w-full bg-primary overflow-hidden">{t11}{t12}</div>;
    $[50] = t11;
    $[51] = t12;
    $[52] = t7;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  return t13;
};
export default BackgroundAgentsPanel;
export { AgentTranscript };
function _temp($) {
  return $.status.running;
}
function _temp2($_0) {
  return $_0.status.failed;
}
function _temp3($_1) {
  return $_1.status.done;
}
function _temp4($_2) {
  return $_2.toolUses;
}
function _temp5($_3) {
  return $_3.viewTranscript;
}
function _temp6(o) {
  return !o;
}
function _temp7($) {
  return $.noTranscript;
}
async function _temp8() {}
function _temp9() {}
function _temp10($) {
  return $.back;
}
function _temp11($_0) {
  return $_0.close;
}
function _temp12(agent_0) {
  return agent_0.running;
}
function _temp13(agent_1) {
  return !agent_1.running;
}
function _temp14($_1) {
  return $_1.title;
}
function _temp15($_2) {
  return $_2.title;
}
function _temp16($_3) {
  return $_3.close;
}
function _temp17($_4) {
  return $_4.empty;
}
function _temp18($_5) {
  return $_5.running;
}
function _temp19($_6) {
  return $_6.finished;
}